import { APP_INITIALIZER, NgModule } from '@angular/core';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { BrowserModule, Title, Meta } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import {
  FacebookLoginProvider,
  SocialLoginModule,
  SocialAuthServiceConfig, SOCIAL_AUTH_CONFIG
} from '@abacritt/angularx-social-login';
import environment from 'src/environments/environment';
import { DatePipe } from '@angular/common';
import { RouterModule } from '@angular/router';
import { RECAPTCHA_V3_SITE_KEY, RecaptchaModule, RecaptchaV3Module, ReCaptchaV3Service } from 'ng-recaptcha-2';
import { AppRoutingModule } from './app-routing.module';

// AppComponent is standalone and bootstrapped via bootstrapApplication

// modules
import { DashboardModule } from './dashboard/dashboard.module';
import { LoginRegisterModule } from './login-register/login-register.module';
import { ReportingSystemModule } from './reporting-system/reporting-system.module';

// services - only import what's needed for APP_INITIALIZER
import { AuthService } from './services/auth.service';
import { UrlRedirectsService } from './services/url-redirects.service';

// general components
import { PageNotFoundComponent } from './errors/page-not-found/page-not-found.component';
import { FiveHundredErrorComponent } from './errors/five-hundred-error/five-hundred-error.component';
import { ApiInterceptor } from './http-interceptors/api-interceptor';
import { ErrorInterceptor } from './http-interceptors/error-interceptor';
import { HttpXsrfInterceptor } from './http-interceptors/xrsf-interceptor';
import { StrapiInterceptor } from './strapi-interceptor.interceptor';
import { AcceptMarketingConsentComponent } from './unauthenticated/accept-markting-consent/accept-marketing-consent.component';

export function initUrls(urls: UrlRedirectsService): () => Promise<void> {
  return () => urls.loadUrls();
}

export function initAuth(auth: AuthService): () => Promise<boolean> {
  return () => auth.checkReturningUser();
}

export function initApp(): () => Promise<void> {
  return () => Promise.resolve();
}

@NgModule({
  declarations: [
  ],
  imports: [
    RouterModule,
    BrowserModule,
    HttpClientModule,
    SocialLoginModule,
    AppRoutingModule,
    DashboardModule,
    LoginRegisterModule,
    BrowserAnimationsModule,
    ReportingSystemModule,
    RecaptchaV3Module
    // removed MaterialModule - standalone components import their own material dependencies
  ],
  providers: [
    Title,
    Meta,
    DatePipe,
    UrlRedirectsService,
    {
      provide: APP_INITIALIZER,
      useFactory: initApp,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initAuth,
      deps: [AuthService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initUrls,
      deps: [UrlRedirectsService],
      multi: true,
    },
    {
      provide: SOCIAL_AUTH_CONFIG,
      useValue: {
        autoLogin: false,
        providers: [
          {
            id: FacebookLoginProvider.PROVIDER_ID,
            provider: new FacebookLoginProvider(environment.facebook.appId, {
              scopes: 'email,first_name,last_name',
            }),
          },
        ],
        onError: (err) => {
          console.log('Social login error', err);
          console.error('Social login error', err);
        }
      } as SocialAuthServiceConfig,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpXsrfInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: StrapiInterceptor,
      multi: true,
    },
    ReCaptchaV3Service,
    { provide: RECAPTCHA_V3_SITE_KEY, useValue: environment.google.recaptcha_site_key },
  ],
  // bootstrap removed; AppComponent is bootstrapped in main.ts
})
export class AppModule {}
